/**
 * HR Followup — track profile-completion follow-ups (Day 7 / Day 14).
 *
 * Tabs over status: active / paused / escalated / completed.
 * Per-row actions: Pause (with reason), Resume, Snooze (1-30d).
 * Owner-only test panel: advance an employee's clock + run cron now.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAuthStore } from '../../lib/store';
import {
  useFollowupList,
  usePauseFollowup,
  useResumeFollowup,
  useSnoozeFollowup,
  useAdvanceFollowupClock,
  useRunFollowupNow,
  type FollowupRow,
} from '../../hooks';
import {
  PauseCircle,
  PlayCircle,
  Clock3,
  AlertOctagon,
  CheckCircle2,
  FlaskConical,
  Rocket,
} from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';

type Tab = 'active' | 'paused' | 'escalated' | 'completed';

const TAB_LABELS: Record<Tab, string> = {
  active: 'กำลังติดตาม',
  paused: 'หยุดชั่วคราว',
  escalated: 'ส่ง To-Do แล้ว',
  completed: 'เสร็จสิ้น',
};

function PctBadge({ pct }: { pct: number }) {
  const cls =
    pct === 100 ? 'bg-green-100 text-green-700'
    : pct >= 60 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return <span className={`badge ${cls}`}>{pct}%</span>;
}

function formatThaiDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function FollowupCard({ row, onChange }: { row: FollowupRow; onChange: () => void }) {
  const pause = usePauseFollowup();
  const resume = useResumeFollowup();
  const snooze = useSnoozeFollowup();
  const advance = useAdvanceFollowupClock();
  const profile = useAuthStore((s) => s.profile);
  const isOwner = profile?.role === 'OWNER';
  const toast = useToast();
  const [promptKind, setPromptKind] = useState<'pause' | 'snooze' | 'advance' | null>(null);
  const [promptVal, setPromptVal] = useState('');

  function openPrompt(kind: 'pause' | 'snooze' | 'advance') {
    setPromptKind(kind);
    setPromptVal(kind === 'pause' ? '' : '7');
  }

  async function handleResume() {
    try {
      await resume.mutateAsync({ emp_code: row.emp_code });
      onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function submitPrompt() {
    try {
      if (promptKind === 'pause') {
        await pause.mutateAsync({ emp_code: row.emp_code, reason: promptVal.trim() || undefined });
      } else if (promptKind === 'snooze') {
        const days = Number(promptVal);
        if (!Number.isInteger(days) || days < 1 || days > 30) { toast.error('ใส่จำนวนวัน 1–30'); return; }
        await snooze.mutateAsync({ emp_code: row.emp_code, days });
      } else if (promptKind === 'advance') {
        const days = Number(promptVal);
        if (!Number.isInteger(days) || days < 1 || days > 60) { toast.error('ใส่จำนวนวัน 1–60'); return; }
        const res = await advance.mutateAsync({ emp_code: row.emp_code, days });
        toast.success(`ปรับ pair_completed_at เป็น ${new Date(res.new_pair_completed_at).toLocaleString('th-TH')} — กด "Run Now" เพื่อรัน cron`);
      }
      setPromptKind(null);
      onChange();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  return (
    <div className="card p-3">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate">
            {row.emp_name}
            <span className="text-gray-400 text-xs font-normal ml-1">· {row.emp_code}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Pair: {formatThaiDateTime(row.pair_completed_at)}
          </div>
        </div>
        <PctBadge pct={row.profile_completion_pct} />
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2 text-xs text-gray-600">
        <div>
          <span className="text-gray-400">Day 7: </span>
          {row.reminder_day7_sent_at ? '✓ ' + formatThaiDateTime(row.reminder_day7_sent_at) : '—'}
        </div>
        <div>
          <span className="text-gray-400">Day 14: </span>
          {row.reminder_day14_sent_at ? '✓ ' + formatThaiDateTime(row.reminder_day14_sent_at) : '—'}
        </div>
      </div>

      {row.followup_status === 'paused' && row.pause_reason && (
        <div className="mt-2 text-xs bg-amber-50 text-amber-800 rounded px-2 py-1">
          หยุดชั่วคราว: {row.pause_reason}
        </div>
      )}
      {row.followup_status === 'escalated' && (
        <div className="mt-2 text-xs bg-purple-50 text-purple-800 rounded px-2 py-1 flex items-center gap-1">
          <AlertOctagon className="w-3 h-3" /> สร้าง To-Do แล้ว (escalation_id: {row.escalation_id})
        </div>
      )}
      {row.followup_status === 'completed' && (
        <div className="mt-2 text-xs bg-green-50 text-green-800 rounded px-2 py-1 flex items-center gap-1">
          <CheckCircle2 className="w-3 h-3" /> ครบ 100% เมื่อ {formatThaiDateTime(row.followup_completed_at)}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-3">
        {row.followup_status === 'active' && (
          <>
            <button onClick={() => openPrompt('pause')} className="btn-secondary text-xs">
              <PauseCircle className="w-3.5 h-3.5" /> หยุดชั่วคราว
            </button>
            <button onClick={() => openPrompt('snooze')} className="btn-secondary text-xs">
              <Clock3 className="w-3.5 h-3.5" /> เลื่อนวัน
            </button>
          </>
        )}
        {row.followup_status === 'paused' && (
          <button onClick={handleResume} className="btn-secondary text-xs">
            <PlayCircle className="w-3.5 h-3.5" /> กลับมาติดตาม
          </button>
        )}
        {isOwner && row.followup_status !== 'completed' && (
          <button
            onClick={() => openPrompt('advance')}
            className="btn-secondary text-xs text-purple-700 border-purple-200 hover:bg-purple-50"
            title="Owner test: ย้อน pair_completed_at"
          >
            <FlaskConical className="w-3.5 h-3.5" /> Test: ย้อนเวลา
          </button>
        )}
      </div>

      <Modal
        open={promptKind !== null}
        onClose={() => setPromptKind(null)}
        title={promptKind === 'pause' ? 'หยุดติดตามชั่วคราว' : promptKind === 'snooze' ? 'เลื่อนวันแจ้งเตือน' : 'Test: ย้อนเวลา pair'}
      >
        <div className="space-y-3">
          {promptKind === 'pause' ? (
            <div>
              <label className="label">เหตุผล (ไม่บังคับ)</label>
              <textarea className="input" rows={2} value={promptVal} onChange={(e) => setPromptVal(e.target.value)} placeholder="เช่น พนักงานลาคลอด" />
            </div>
          ) : (
            <div>
              <label className="label">{promptKind === 'snooze' ? 'เลื่อนไปอีกกี่วัน (1–30)' : 'ย้อนเวลาไปอีกกี่วัน (1–60)'}</label>
              <input className="input" inputMode="numeric" value={promptVal} onChange={(e) => setPromptVal(e.target.value)} />
            </div>
          )}
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setPromptKind(null)}>ยกเลิก</button>
            <button className="btn-primary flex-1" onClick={submitPrompt} disabled={pause.isPending || snooze.isPending || advance.isPending}>ยืนยัน</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

/** Content-only version used by the People hub. */
export function HRFollowupContent() {
  const [tab, setTab] = useState<Tab>('active');
  const { data, isLoading, refetch } = useFollowupList(tab);
  const runNow = useRunFollowupNow();
  const profile = useAuthStore((s) => s.profile);
  const isOwner = profile?.role === 'OWNER';
  const toast = useToast();
  const confirm = useConfirm();

  async function handleRunNow() {
    if (!(await confirm({ title: 'รัน cron ติดตามประวัติทันที?', message: 'จะส่ง LINE จริงถ้ามีคนถึง Day 7/14' }))) return;
    try {
      const res = await runNow.mutateAsync();
      toast.success(`รันเสร็จ — Day 7: ${res.day7_sent}, Day 14: ${res.day14_sent}, errors: ${res.errors}`);
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  const rows = data?.rows ?? [];

  return (
    <>
      <div className="flex items-center justify-between mb-3 gap-2">
        <p className="text-sm text-gray-600">ติดตามให้พนักงานกรอกประวัติให้ครบหลังจาก pair LINE</p>
        {isOwner && (
          <button
            onClick={handleRunNow}
            disabled={runNow.isPending}
            className="btn-secondary text-xs shrink-0"
            title="Owner only: force-run cron now"
          >
            <Rocket className="w-4 h-4" />
            {runNow.isPending ? 'กำลังรัน…' : 'Run Now'}
          </button>
        )}
      </div>
      {/* Tabs */}
      <div className="flex gap-1 mb-3 border-b border-gray-200 overflow-x-auto">
        {(Object.keys(TAB_LABELS) as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition ${
              tab === t
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-gray-500">กำลังโหลด…</p>
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-gray-500">ไม่มีรายการในกลุ่มนี้</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {rows.map((row) => (
            <FollowupCard key={row.emp_code} row={row} onChange={refetch} />
          ))}
        </div>
      )}
    </>
  );
}

/** Standalone page kept for legacy /hr/followup (now redirects via People hub). */
export function HRFollowup() {
  return (
    <AdminLayout title="ตามเก็บประวัติพนักงาน">
      <HRFollowupContent />
    </AdminLayout>
  );
}
